import { useTranslation } from "@lib/useTypedTranslation";

import { Link } from 'react-router-dom';
import { isNil } from 'lodash';

import { IDevicesData } from "../../../../services/core/devices";
import { formatDateLastUpdated, formatValue, showDeviceStatusTooltip, getFormatPercentageToLocale, getFormatSecondsToHoursToLocale, getFormatDataUsageToGBToLocale } from "../../../../components/data-table/dataFormatters";
import { encode } from "../../../../lib/base64";
import { ITableColumn } from "../../../../components/data-table/types";
import { useWorldRoutes } from "../../../../routes/parts/allWorldRoutes";
import { Tooltip } from "../../../../components/tooltip/tooltip";
import { useWorldSettingsContext } from '../../../../context/worldSettings';
import { rearrangeList } from '../../../../lib/rearrangeList';
import { useMemo } from "react";
import { GroupListFilter, HomeLocationsListFilter, ManufacturerListFilter, ModelListFilter } from "./filters/devicesListFilters";
import { AlertsFilter } from "@components/data-table/filters/alertsFilter";
import { useLanguageAndLocaleContext } from "../../../../context/languageAndLocale";

const getDeviceIdLink = (value: any, rowData: IDevicesData, path: string) => {
  if (isNil(rowData)) {
    return formatValue(null);
  }
  return <Link to={`${path}?id=${encodeURIComponent(encode(rowData.id))}`} className="core-devices-list_link">{formatValue(value)}</Link>;
};

export const useDevicesListColumns = () => {
  const ns = 'translation';
  const { t } = useTranslation(ns);
  const { worldSettings } = useWorldSettingsContext();
  const routes = useWorldRoutes();
  const { locale } = useLanguageAndLocaleContext();

  const columns = useMemo(() => {
    const mobileTitleWithTooltip = <>
      {t('MOBILE_DATA')}
      <Tooltip key='mobile-tooltip' popoverPortalSelector={'.core-devices-list'} placement={'bottom'} popperContent={<div className="core-devices_list-data_usage-popover_text">{t('MOBILE_DATA_TOOLTIP_TEXT')}</div>}>
        <i aria-label='about mobile data' className="core-devices_list-data_usage-icon fa fa-question-circle" />
      </Tooltip>
    </>;
    const wifiTitleWithTooltip = <>
      {t('WIFI_DATA')}
      <Tooltip key='wifi-tooltip' popoverPortalSelector={'.core-devices-list'} placement={'bottom'} popperContent={<div className="core-devices_list-data_usage-popover_text">{t('WIFI_DATA_TOOLTIP_TEXT')}</div>}>
        <i aria-label='about wifi data' className="core-devices_list-data_usage-icon fa fa-question-circle" />
      </Tooltip>
    </>;

    function getDeviceStatusTooltipProcessor(value: any, rowData: IDevicesData) {
      return showDeviceStatusTooltip(value, rowData, routes.core.deviceOverview);
    }
    function getDeviceIdLinkProcessor(value: any, rowData: IDevicesData) {
      return getDeviceIdLink(value, rowData, routes.core.deviceOverview);
    }
    function getHomeLocationName(value: string, rowData: IDevicesData) {
      return rowData.homeLocation ? formatValue(rowData.homeLocation.name) : "-";
    }

    const columns: ITableColumn[] = [
      { id: 'serialNumber', title: t('SERIAL_NUMBER'), dataPath: 'serialNumber', sortable: true },
      { id: 'assetTag', title: t('ASSET_TAG'), dataPath: 'assetTag', sortable: true, processor: formatValue, textAlign: 'left', minWidth: '10rem' },
      { id: 'imei', title: t('IMEI'), dataPath: 'imei', sortable: true, processor: formatValue, minWidth: '10rem' },
      { id: 'deviceName', title: t('DEVICE_NAME'), dataPath: 'deviceName', sortable: true, processor: formatValue, textAlign: 'left' },
      { id: 'manufacturer', title: t('MANUFACTURER'), dataPath: 'manufacturer', sortable: true, processor: formatValue, FilterForm: ManufacturerListFilter },
      { id: 'model', title: t('MODEL'), dataPath: 'model', sortable: true, processor: formatValue, FilterForm: ModelListFilter, minWidth: '8rem' },
      { id: 'group', title: t('GROUPS'), dataPath: 'group', processor: formatValue, FilterForm: GroupListFilter, minWidth: '10rem' },
      { id: 'homeLocation', title: t('HOME_LOCATION'), dataPath: 'homeLocation.id', sortable: true, processor: getHomeLocationName, FilterForm: HomeLocationsListFilter },
      { id: 'updated', title: t('LAST_SEEN'), dataPath: 'updated', processor: formatDateLastUpdated, sortable: true, minWidth: '8rem' },
      { id: 'mobileDataUsage', title: mobileTitleWithTooltip, dataPath: 'mobileDataUsage', sortable: true, processor: getFormatDataUsageToGBToLocale(locale), textAlign: 'right' },
      { id: 'wifiDataUsage', title: wifiTitleWithTooltip, dataPath: 'wifiDataUsage', sortable: true, processor: getFormatDataUsageToGBToLocale(locale), textAlign: 'right' },
      { id: 'percentageUsed', title: t('STORAGE_USED'), dataPath: 'storage.percentageUsed', sortable: true, processor: getFormatPercentageToLocale(locale), textAlign: 'right' },
      { id: 'averageDischargeRate', title: t('AVERAGE_DISCHARGE_RATE', { ns: 'batteryEssentialsDeviceView' }), dataPath: 'averageDischarge', sortable: true, processor: getFormatPercentageToLocale(locale, 2), textAlign: 'right' },
      { id: 'averageDischargeTime', title: t('AVERAGE_DISCHARGE_TIME'), dataPath: 'averageDischargeTime', sortable: true, processor: getFormatSecondsToHoursToLocale(locale), textAlign: 'right' },
      { id: 'firmware', title: t('FIRMWARE_VERSION'), dataPath: 'firmware.version', sortable: true, processor: formatValue, textAlign: 'left', minWidth: '10rem' },
      { id: 'operatingSystem', title: t('OS_VERSION'), dataPath: 'operatingSystem.buildVersion', sortable: true, processor: formatValue, textAlign: 'left' },
      { id: 'biosVersion', title: t('BIOS_VERSION'), dataPath: 'bios.version', sortable: true, processor: formatValue, textAlign: 'left' },
      { id: 'clientVersion', title: t('CLIENT_VERSION'), dataPath: 'clientVersion', sortable: true, processor: formatValue, textAlign: 'left' },
      { id: 'extensionVersion', title: t('EXTENSION_VERSION_DEVICE'), dataPath: 'extensionVersion', sortable: true, processor: formatValue, textAlign: 'left' },
      { id: 'deviceStatus', title: t('STATUS'), dataPath: 'deviceStatus', processor: getDeviceStatusTooltipProcessor, textAlign: 'center', FilterForm: AlertsFilter }
    ];
    const deviceIdentifier = worldSettings.deviceIdentifier;

    const updatedColumn: ITableColumn[] = rearrangeList(columns, deviceIdentifier);
    updatedColumn[0].processor = getDeviceIdLinkProcessor;

    return updatedColumn;
  }, [worldSettings, routes.core.deviceOverview, t, locale]);
  return columns;
};
